import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Flex } from 'antd'
import { Image } from 'react-bootstrap'
import className from 'clsx'
import useLang from '@/utils/hooks/use-lang'
import Icon from '@/components/icon'
import FullScreenBanner from '@/components/full-screen-banner'
import PageWrapper from '@/components/page-wrapper'
import ContentList from '@/components/content-list'
import { i18nSolutionCustom } from '@/assets/content/solution'
import { SolutionGreenCityProps } from '@/assets/content/solution/green-city'
import ScrollGallery from '@/components/scroll-gallery'
import ImageText from '../new-items/image-text'
import FeatureIcons from '../new-items/feature-icons'
import Styles from './style.module.scss'

export default function GreenCity() {
  const { type } = useParams()
  const [lang] = useLang()
  const content: SolutionGreenCityProps = i18nSolutionCustom?.[lang].find(({ path }) => path === `/solution/${type}`)
  const { banner, title, description, children } = content || {}
  const navigate = useNavigate()

  const { search } = useLocation()
  const active = +new URLSearchParams(search).get('active') || 0

  const jumpTo = (index: number) => {
    navigate(`/solution/${type}?active=${index}`)
  }

  const activeItem = children[active]

  const {
    subBanner,
    featureBanner,
    title: subTitle,
    featureInfo,
    featureIcons,
    imageText,
    applications,
    advantages,
    footer,
  } = activeItem || {}

  const stickyRef = useRef<HTMLDivElement>(null)
  const [isSticky, setIsSticky] = useState(false)
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(stickyRef.current?.getBoundingClientRect().top <= 60)
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <PageWrapper soul key={active}>
      <FullScreenBanner image={banner} className={Styles.bannerWrapper} vertical>
        <div className={Styles.content}>
          <h1>{title}</h1>
          <div className={Styles.description}>{description}</div>
        </div>
      </FullScreenBanner>
      <div ref={stickyRef} />
      <Flex justify='space-evenly' className={className(Styles.subSolutionTabs, isSticky && Styles.sticky)}>
        {children.map((d, index) => (
          <Flex
            key={d.icon}
            vertical
            align='center'
            justify='center'
            className={className(Styles.tabItem, active === index && Styles.active)}
            onClick={() => jumpTo(index)}
          >
            <Icon name={d.icon} className={Styles.tabIcon} />
            <div className={Styles.tabLabel}>{d.iconLabel}</div>
          </Flex>
        ))}
      </Flex>
      {!!subBanner && <Image src={subBanner} alt='subBanner' className={Styles.subBanner} />}
      <Flex vertical className={Styles.contentWrapper}>
        {featureBanner && (
          <Flex vertical align='center' className={Styles.content}>
            {!!subTitle && <h2 className={Styles.contentItemTitle}>{subTitle}</h2>}
            {!!featureBanner && <Image src={featureBanner} alt={subTitle} />}
            <Flex vertical className={Styles.featureInfo}>
              {!!featureInfo?.title && <div className={Styles.featureInfoTitle}>{featureInfo?.title}</div>}
              {!!featureInfo?.items?.length && (
                <ul>
                  {featureInfo?.items?.map((item, index) => (
                    <li key={index}>{`${index + 1}. ${item}`}</li>
                  ))}
                </ul>
              )}
            </Flex>
            <FeatureIcons data={featureIcons} />
          </Flex>
        )}
        {!!imageText?.length && <ImageText data={imageText} />}
      </Flex>
      {!!applications?.length && (
        <div className={Styles.applicationWrapper}>
          <h2>Application</h2>
          <ScrollGallery
            cards={applications}
            renderCard={({ title, description, image }) => (
              <Flex
                vertical
                justify='flex-end'
                key={title}
                className={Styles.applicationItem}
                style={{ background: `url(${image}) no-repeat center / cover` }}
              >
                <div className={Styles.applicationLabel}>{title}</div>
                <div className={Styles.applicationDescription}>{description}</div>
              </Flex>
            )}
          />
        </div>
      )}
      {advantages && (
        <div className={Styles.sectionWrapper}>
          <h2>{advantages.title}</h2>
          <ContentList items={advantages.items || []} image={advantages.image} />
        </div>
      )}
      {footer && <Image src={footer.banner} />}
    </PageWrapper>
  )
}
